import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { trackEvent } from '../../../actions/analyticsActions';

import styles from './Panel.css';
import EmptyPanel from '../EmptyPanel';
import { useAppSelector } from '../../../utils';
import { ExpandButton } from '..';

export type PanelProps = {
  index?: number;
  panel?: React.ReactElement;
};

export const Panel = ({ index, panel }: PanelProps) => {
  const dispatch = useDispatch();
  const isSplitViewEnabled = useAppSelector(state => state.layers.isSplitViewEnabled);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  useEffect(() => {
    if (!isSplitViewEnabled) {
      setIsCollapsed(true);
    }
  }, [isSplitViewEnabled]);


  const collapsePanel = useCallback(() => setIsCollapsed(true), []);

  const toggle = useCallback(() => {
    dispatch(trackEvent('split_view', isCollapsed ? 'expanded' : 'collapsed'));

    setIsCollapsed(prevVal => !prevVal);
  }, [dispatch, isCollapsed]);

  const overlayToggle = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target === e.currentTarget && !isCollapsed) {
      toggle();
    }
  }, [isCollapsed, toggle]);

  const panelStylesArray = [styles.panelContainer];
  let overlayStyles = '';

  if (isSplitViewEnabled) {
    panelStylesArray.push(styles.panelContainerSplit);
    // The overlayContainer style is added to fix a bug in Safari/Webkit where
    // the parent div is not repainted when the DOM is updated. See: CFVWEB-708
    // for more info.
    overlayStyles = styles.overlayContainer;
    if (isCollapsed) {
      panelStylesArray.push(styles.collapsed);
    } else {
      panelStylesArray.push(styles.expanded);
      overlayStyles = styles.panelOverlay;
    }
  }
  const panelStyles = panelStylesArray.join(' ');

  if (!panel) {
    return <EmptyPanel panelStyles={panelStyles} />;
  }

  const panelNode =
    panel &&
    React.cloneElement(panel, {
      collapsePanel,
      index,
      isCollapsed,
      isSplitViewEnabled,
    });

  return (
    <div
      className={overlayStyles}
      onClick={overlayToggle}
      role="button"
      tabIndex={0}
    >
      <div className={panelStyles} data-test-id="dmb-panel">
        {isSplitViewEnabled && (
          <ExpandButton isCollapsed={isCollapsed} onClick={toggle} />
        )}
        {panelNode}
      </div>
    </div>
  );
};
